// Recommended row under the player (spec video-detail-redesign US-12): other collections of this album's arena,
// then of its tags, from consumer-service getVodByTag (the same source as a row's "See all" page).

export const REC_LIMIT = 12;
// Fewer arena / tag cards than this: the row is filled with the network's newest albums (getRecommendedAlbums).
export const MIN_ARENA_CARDS = 4;
const MAX_SOURCES = 3;

export interface RecCard {
  id: number;
  title: string;
  image: string;
  slug: string;
  category: string;
}

// "7_album" -> 7
function rowId(key: any): number {
  return Number(String(key || '').split('_')[0]) || 0;
}

/** Rows to read, arena first, then the album's tags (the album record's genre_id / tag_id / tag_ids). */
export function recSourceIds(album: any): number[] {
  const ids = [rowId(album?.genre_id), rowId(album?.tag_id), ...(album?.tag_ids || []).map(Number)];
  return ids.filter((id, i) => id > 0 && ids.indexOf(id) === i).slice(0, MAX_SOURCES);
}

/** Row title by where its cards came from: all from the arena, a mix ("More like this"), or none (network / history picks). */
export function recTitle(album: any, arenaCards: number, totalCards: number): string {
  if (!arenaCards) return 'You might also like';
  const arena = Array.isArray(album?.genre) ? album.genre[0] : album?.genre;
  return arena && arenaCards === totalCards ? `More from ${arena}` : 'More like this';
}

function visibleIn(item: any, countryCode: string): boolean {
  const raw = item?.metadata?.additional_info;
  if (!raw) return true;
  try {
    const geo = (typeof raw === 'string' ? JSON.parse(raw) : raw)?.geo_block;
    return !geo || geo.includes('all') || geo.includes(countryCode);
  } catch {
    return true;
  }
}

/** Cards from getVodByTag pages, in order, without repeats, the current album or albums blocked in this country. */
export function toRecCards(pages: any[][], currentId: number, countryCode: string, fallbackImage = ''): RecCard[] {
  const cards: RecCard[] = [];
  const seen = new Set<number>([Number(currentId) || 0]);
  for (const item of ([] as any[]).concat(...pages)) {
    const info = item?.stream_info || {};
    const id = Number(info.id || info.streamid) || 0;
    if (!id || seen.has(id) || !info.slug || !visibleIn(item, countryCode)) continue;
    seen.add(id);
    cards.push({
      id, title: info.stream_video_title || '', image: info.video_thumbnail_image || fallbackImage,
      slug: info.slug, category: info.vod_video_audio || 'video',
    });
    if (cards.length === REC_LIMIT) break;
  }
  return cards;
}

/** getRecommendedAlbums result: the network's albums, ranked by the viewer's watch history when personalised (US-13). */
export interface NetworkRecs {
  items: any[];
  personalised: boolean;
}

/**
 * The row: watch-history picks first when personalised; otherwise the arena / tags, filled with the
 * network's newest when they give fewer than MIN_ARENA_CARDS.
 */
export function buildRecRow(album: any, arenaPages: any[][], network: NetworkRecs | null, currentId: number,
  countryCode: string, fallbackImage = ''): { cards: RecCard[]; title: string } {
  const arenaCards = toRecCards(arenaPages, currentId, countryCode, fallbackImage).length;
  if (network?.personalised && network.items.length) {
    return { cards: toRecCards([network.items, ...arenaPages], currentId, countryCode, fallbackImage), title: 'You might also like' };
  }
  const pages = network && arenaCards < MIN_ARENA_CARDS ? [...arenaPages, network.items] : arenaPages;
  const cards = toRecCards(pages, currentId, countryCode, fallbackImage);
  return { cards, title: recTitle(album, arenaCards, cards.length) };
}
